import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { useApiClient } from "@/api/client"
import { useWorkspace } from "@/workspaces/WorkspaceContext"

export type SearchResultType = "task" | "project" | "file" | "doc" | "person"

export type GlobalSearchResult = {
  type: SearchResultType
  id: string
  title: string
  subtitle?: string
  url: string
  snippet?: string
  meta?: Record<string, string>
}

export function useDebouncedValue<T>(value: T, delay = 225) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])

  return debounced
}

export function useGlobalSearch(query: string, types: SearchResultType[], limit = 20) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const trimmed = query.trim()
  const enabled = trimmed.length >= 2
  const typeParam = types.join(",")

  return useQuery({
    queryKey: ["global-search", workspace.slug, trimmed, typeParam, limit],
    queryFn: () => {
      const params = new URLSearchParams({
        q: trimmed,
        types: typeParam,
        limit: String(limit),
      })
      return fetchWithAuth<{ results: GlobalSearchResult[] }>(
        `/api/v1/workspaces/${workspace.slug}/search?${params.toString()}`,
      )
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })
}
